import React, { useCallback, useMemo, useState } from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import {
  getCategoryIdsByTypes,
  CategoryType,
  getElementCategory,
} from '@shared/helpers/element-category.helpers';
import { useGroupedElementCategories } from '@features/elements/hooks/use-grouped-element-categories.hook';
import { ElementCategoryMenu } from '@features/elements/components/ElementCategoryMenu/ElementCategoryMenu';
import { useObjectMemo } from '@client/hooks/use-object.hooks';
import { buildCategoryPickerElement } from '@shared/helpers/generic-product-picker.helpers';

const GENERIC_PRODUCT_CATEGORY_IDS: ElementCategoryID[] = getCategoryIdsByTypes(
  CategoryType.Product,
  CategoryType.Service,
).filter((categoryId) => categoryId !== ElementCategoryID.Labour);

type GenericProductCategorySelectProps = {
  categoryId?: ElementCategoryID;
  onCategoryChange: (categoryId: ElementCategoryID) => void;
};

/**
 * Category picker for {@link EditProductDialog} without project mutations.
 */
export const GenericProductCategorySelect: React.FC<
  GenericProductCategorySelectProps
> = ({ categoryId, onCategoryChange }) => {
  const pickerElement = useMemo(
    () => buildCategoryPickerElement(categoryId),
    [categoryId],
  );
  const categoryGroups = useGroupedElementCategories(
    pickerElement,
    GENERIC_PRODUCT_CATEGORY_IDS,
  );
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const selectedCategoryName = useMemo(
    () =>
      categoryId
        ? (getElementCategory(categoryId)?.name ?? 'Select category')
        : 'Select category',
    [categoryId],
  );

  const slotProps = useObjectMemo<NonNullable<TextFieldProps['slotProps']>>({
    input: { readOnly: true },
    htmlInput: { sx: { cursor: 'pointer' } },
  });

  const handleSelect = useCallback(
    (id: ElementCategoryID) => {
      onCategoryChange(id);
      setAnchor(null);
    },
    [onCategoryChange],
  );

  return (
    <>
      <TextField
        fullWidth
        size="small"
        value={selectedCategoryName}
        onClick={(event) => setAnchor(event.currentTarget)}
        slotProps={slotProps}
      />
      <ElementCategoryMenu
        anchor={anchor}
        width="anchorEl"
        selectedCategoryId={categoryId ?? ElementCategoryID.None}
        categoryGroups={categoryGroups}
        onSelect={handleSelect}
        onClose={() => setAnchor(null)}
      />
    </>
  );
};
